import { format, isSameDay, isBefore } from 'date-fns';

import { TypedObject, Calendar } from '@/definitions';

export const readableDate = (rawDate: Date, locale: Locale, dateFormat?: string): string => {
  return format(rawDate, dateFormat ?? 'PPPP', { locale });
}

export const moneySeperator = (amount: string, locale: string): string => {
  return amount.replace(',', '.').split('.').join(locale === 'nl' ? ',' : '.');
}

export const arrayIntersect = (haystack: any[], needle: any[]): boolean => {
  return needle.some((item: any) => {
    return haystack.includes(item);
  });
};

export const asyncForEach = async (array: any[], callback: Function) => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
};

export const deepEqual = (left: unknown, right: unknown): boolean => {
  if (typeof left !== typeof right) return false;
  if (typeof left !== 'object') return left === right;
  if (Array.isArray(left)) {
    if (!Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every(leftItem => right.some(rightItem => deepEqual(leftItem, rightItem)));
  }

  return Object.keys(left as TypedObject).every((key: string) => {
    return deepEqual((left as TypedObject)[key], (right as TypedObject)[key])
  });
};

export const deepMerge = (newKeys: TypedObject, oldItem: unknown): typeof oldItem => {
  const item = oldItem as TypedObject;
  Object.keys(newKeys).forEach(key => {
    if (item[key]) {
      if (typeof newKeys[key] !== typeof item[key]) {
        item[key] = newKeys[key];
      }
      if (typeof newKeys[key] !== 'object') {
        if (!deepEqual(newKeys[key], item[key])) {
          item[key] = newKeys[key];
        }
      } else {
        if (Array.isArray(newKeys[key])) {
          item[key] = newKeys[key];
        } else {
          item[key] = deepMerge(newKeys[key], item[key]);
        }
      }
    } else {
      item[key] = newKeys[key];
    }
  });
  return item;
}

export const isOffDay = (date: Date, calendar: Calendar) => {
  return Boolean(calendar.additionalDays.find(day => isSameDay(new Date(day.date), date)));
}

export const translateWeekdays = (calendar: Calendar): number[] => {
  const numByDay = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
  const numberedWorkingDays: number[] = [];
  if (calendar.workingDays.monday) numberedWorkingDays.push(numByDay.monday);
  if (calendar.workingDays.tuesday) numberedWorkingDays.push(numByDay.tuesday);
  if (calendar.workingDays.wednesday) numberedWorkingDays.push(numByDay.wednesday);
  if (calendar.workingDays.thursday) numberedWorkingDays.push(numByDay.thursday);
  if (calendar.workingDays.friday) numberedWorkingDays.push(numByDay.friday);
  if (calendar.workingDays.saturday) numberedWorkingDays.push(numByDay.saturday);
  if (calendar.workingDays.sunday) numberedWorkingDays.push(numByDay.sunday);
  return numberedWorkingDays;
}

export const findDate = (amount: number, date: Date, calendar: Calendar): Date => {
  let d = new Date(date.setDate(date.getDate() + amount));
  return translateWeekdays(calendar).includes(d.getDay()) && !isOffDay(d, calendar) ? d : findDate(amount || 1, d, calendar);
}